import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Rating,
  IconButton,
  Modal,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button as MuiButton
} from '@mui/material';
import { Search, Visibility, Close, FilterList, FileDownload } from '@mui/icons-material';
import * as XLSX from 'xlsx'; // Import the Excel library
import baseurl from '../Baseurl/baseurl';
import { Card, Eyebrow, Button, StatusBadge, EmptyState } from '../ui';

// Aurora token map (CSS vars flip per theme — works in both light & dark).
const T = {
  ink: 'var(--text-c)',
  text: 'var(--text-dim)',
  muted: 'var(--text-faint)',
  line: 'var(--stroke)',
  panelStrong: 'var(--panel-strong)',
  green: 'var(--green)',
  greenSoft: 'var(--green-soft-2)',
  inputBg: 'var(--input-bg)',
};

const REVIEW_PAGE_SIZE = 10;

const ReviewsTestimonialsManagement = () => {
  const [activeTab, setActiveTab] = useState('All Reviews');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewsData, setReviewsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState('reviewer');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);

  const tabs = ['All Reviews', 'Pending', 'Approved', 'Rejected'];

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${baseurl}/api/ratings/all`, {
        method: 'GET',
        credentials: 'include',
      });
      const result = await response.json();
      if (result.data) {
        const mapped = result.data.map((item) => ({
          id: item.rid,
          reviewer: `${item.ratedBy.first_name} ${item.ratedBy.last_name}`,
          email: item.ratedBy.email,
          avatar: item.ratedBy.first_name[0],
          avatarColor: '#1976d2',
          business: item.business.company_name,
          businessType: 'Business',
          serviceType: 'Service',
          rating: item.rating,
          review: item.message,
          status: item.status.charAt(0).toUpperCase() + item.status.slice(1)
        }));
        setReviewsData(mapped);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Handle export to Excel
  const handleExport = () => {
    // Prepare data for export
    const exportData = filteredReviews.map(review => ({ "Reviewer": review.reviewer, "Email": review.email, "Business": review.business, "Business Type": review.businessType, "Service Type": review.serviceType, "Rating": review.rating, "Review": review.review, "Status": review.status
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reviews");

    // Generate Excel file and download
    XLSX.writeFile(workbook, "reviews_and_testimonials.xlsx");
  };

  const handleStatusChange = async (id, newStatus) => {
    setLoading(true);
    try {
      const response = await fetch(`${baseurl}/api/${id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (response.ok) {
        setReviewsData((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, status: newStatus.charAt(0).toUpperCase() + newStatus.slice(1) } : r
          )
        );
      } else {
        console.error('Error updating status:', data.error);
      }
    } catch (err) {
      console.error('Request error:', err);
    }
    setLoading(false);
  };

  const filteredReviews = reviewsData.filter((review) => {
    const matchesTab = activeTab === 'All Reviews' || review.status === activeTab;
    const matchesSearch = review.reviewer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.business.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  }).sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'reviewer':
        aValue = a.reviewer.toLowerCase();
        bValue = b.reviewer.toLowerCase();
        break;
      case 'business':
        aValue = a.business.toLowerCase();
        bValue = b.business.toLowerCase();
        break;
      case 'rating':
        aValue = a.rating;
        bValue = b.rating;
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        aValue = a.reviewer.toLowerCase();
        bValue = b.reviewer.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination logic
  const totalFiltered = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / REVIEW_PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * REVIEW_PAGE_SIZE;
  const endIndex = Math.min(startIndex + REVIEW_PAGE_SIZE, totalFiltered);
  const paginatedReviews = filteredReviews.slice(startIndex, endIndex);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, activeTab, sortBy, sortOrder]);

  // Keep page in valid range
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const handlePageChange = (event, newPage) => {
    if (typeof newPage === 'number' && newPage >= 1) {
      setPage(Math.min(newPage, totalPages));
    }
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReview(null);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 800,
    bgcolor: T.panelStrong,
    backgroundImage: 'none',
    border: `1px solid ${T.line}`,
    backdropFilter: 'blur(18px)',
    color: T.ink,
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-card)',
    p: 4,
    maxHeight: '90vh',
    overflow: 'auto'
  };

  // Rating stars rendered in the token primary (green) so they glow in both modes.
  const ratingSx = {
    '& .MuiRating-iconFilled': { color: T.green },
    '& .MuiRating-iconEmpty': { color: T.line },
  };

  return (
    <div className="space-y-6">
      {/* Page head */}
      <div className="mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Eyebrow>Reviews</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Reviews &amp; <em className="kbc-serif">Testimonials</em>
          </h1>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">
            Review and approve testimonials from the business directory.
          </p>
        </div>
        <Button variant="ghost" leftIcon={<FileDownload fontSize="small" />} onClick={handleExport}>
          Export
        </Button>
      </div>

      {/* Controls + table */}
      <Card padding="none" className="overflow-hidden">
        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 border-b border-line/12 px-4 py-4 sm:px-6">
          {tabs.map((tab) => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={
                  'rounded-full px-4 py-1.5 text-sm font-semibold transition-all duration-200 ' +
                  (active
                    ? 'bg-primary text-onprimary shadow-btn'
                    : 'bg-primary/5 text-text ring-1 ring-inset ring-line/15 hover:bg-primary/10 hover:text-ink')
                }
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Search / filter / sort */}
        <Box sx={{
          p: 3,
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' }
        }}>
          <TextField
            placeholder="Search reviews..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: T.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: { xs: 1, md: 0 },
              minWidth: { md: 300 },
              '& .MuiOutlinedInput-root': {
                bgcolor: T.inputBg,
                color: T.ink,
                '& fieldset': { borderColor: T.line },
                '&:hover fieldset': { borderColor: T.green },
                '&.Mui-focused fieldset': { borderColor: T.green }
              },
              '& .MuiInputBase-input::placeholder': { color: T.muted, opacity: 1 }
            }}
          />
          <MuiButton
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterDialogOpen(true)}
            sx={{
              color: T.text,
              borderColor: T.line,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: T.green, bgcolor: T.greenSoft }
            }}
          >
            Filter
          </MuiButton>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ color: T.muted, '&.Mui-focused': { color: T.green } }}>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
              sx={{
                color: T.ink,
                bgcolor: T.inputBg,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.green },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.green },
                '& .MuiSvgIcon-root': { color: T.muted }
              }}
            >
              <MenuItem value="reviewer">Reviewer</MenuItem>
              <MenuItem value="business">Business</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>
          <MuiButton
            variant="outlined"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            sx={{
              color: T.text,
              borderColor: T.line,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: T.green, bgcolor: T.greenSoft }
            }}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </MuiButton>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: T.greenSoft }}>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Reviewer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Business/Member</TableCell>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Service Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Rating</TableCell>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Review</TableCell>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: T.ink, borderColor: T.line }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedReviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, borderColor: T.line }}>
                    <EmptyState title="No reviews found" description="Try adjusting your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReviews.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '& td': { borderColor: T.line, color: T.text },
                      '&:hover': { backgroundColor: T.greenSoft }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: T.green, color: 'var(--on-primary, #04221a)', fontWeight: 700 }}>{row.avatar}</Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: T.ink }}>{row.reviewer}</Typography>
                          <Typography variant="caption" sx={{ color: T.muted }}>{row.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: T.text }}>{row.business}</TableCell>
                    <TableCell sx={{ color: T.text }}>{row.serviceType}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={row.rating} readOnly size="small" sx={ratingSx} />
                        <Typography sx={{ color: T.text }}>{row.rating}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, color: T.text }}>{row.review}</TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      {row.status === 'Pending' ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="sm"
                            variant="primary"
                            loading={loading}
                            disabled={loading}
                            onClick={() => handleStatusChange(row.id, 'approved')}
                          >
                            {loading ? 'Loading...' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={loading}
                            disabled={loading}
                            onClick={() => handleStatusChange(row.id, 'rejected')}
                          >
                            {loading ? 'Loading...' : 'Reject'}
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewReview(row)}
                          leftIcon={<Visibility fontSize="small" />}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 }
        }}>
          <Typography variant="body2" sx={{ color: T.muted }}>
            {totalFiltered === 0
              ? 'Showing 0 reviews'
              : `Showing ${startIndex + 1}-${endIndex} of ${totalFiltered} reviews (${REVIEW_PAGE_SIZE} per page)`}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': { color: T.text },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: T.green,
                color: 'var(--on-primary, #04221a)'
              }
            }}
          />
        </Box>
      </Card>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: T.panelStrong, backgroundImage: 'none', border: `1px solid ${T.line}`, borderRadius: 'var(--radius)', backdropFilter: 'blur(18px)', color: T.ink } }}
      >
        <DialogTitle sx={{ color: T.ink, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>Filter Reviews</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: T.muted, '&.Mui-focused': { color: T.green } }}>Status</InputLabel>
                <Select
                  value={activeTab === 'All Reviews' ? 'All' : activeTab}
                  label="Status"
                  onChange={(e) => {
                    const value = e.target.value;
                    setActiveTab(value === 'All' ? 'All Reviews' : value);
                  }}
                  sx={{
                    color: T.ink,
                    bgcolor: T.inputBg,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.green },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.green },
                    '& .MuiSvgIcon-root': { color: T.muted }
                  }}
                >
                  <MenuItem value="All">All Reviews</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${T.line}` }}>
          <MuiButton onClick={() => setFilterDialogOpen(false)} sx={{ color: T.text }}>Cancel</MuiButton>
          <Button onClick={() => setFilterDialogOpen(false)}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          {selectedReview && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ color: T.ink, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600 }}>Review Details</Typography>
                <IconButton onClick={handleCloseModal} sx={{ color: T.text }}><Close /></IconButton>
              </Box>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: T.green, color: 'var(--on-primary, #04221a)', fontWeight: 700, width: 60, height: 60 }}>
                      {selectedReview.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ color: T.ink }}>{selectedReview.reviewer}</Typography>
                      <Typography variant="body2" sx={{ color: T.muted }}>{selectedReview.email}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2" sx={{ color: T.muted }}>Business</Typography>
                  <Typography sx={{ color: T.ink }}>{selectedReview.business}</Typography>
                  <Typography variant="subtitle2" sx={{ color: T.muted, mt: 1 }}>Business Type</Typography>
                  <Typography sx={{ color: T.ink }}>{selectedReview.businessType}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2" sx={{ color: T.muted }}>Service Type</Typography>
                  <Typography sx={{ color: T.ink }}>{selectedReview.serviceType}</Typography>
                  <Typography variant="subtitle2" sx={{ color: T.muted, mt: 1 }}>Rating</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={selectedReview.rating} readOnly sx={ratingSx} />
                    <Typography sx={{ color: T.ink }}>{selectedReview.rating}</Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" sx={{ color: T.muted, mb: 1 }}>Review</Typography>
                  <Typography sx={{ p: 2, bgcolor: T.greenSoft, border: `1px solid ${T.line}`, borderRadius: 1, color: T.text }}>
                    {selectedReview.review}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <StatusBadge status={selectedReview.status} />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default ReviewsTestimonialsManagement;
